import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Input, List, Avatar, Typography } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { SearchOutlined, TeamOutlined } from '@ant-design/icons';
import './CommandPalette.css';

const { Text } = Typography;

// Command types
const COMMANDS = {
  SEARCH_ORGS: 'search_orgs',
};

// Command definitions with metadata
const COMMAND_DEFINITIONS = [
  {
    id: COMMANDS.SEARCH_ORGS,
    title: 'Search Organizations',
    description: 'Find and navigate to an organization',
    icon: <TeamOutlined />,
    shortcut: '→',
  },
];

const mapStateToProps = (state) => {
  if (state) {
    return {
      user: state.user,
    };
  }
  return null;
};

export const CommandPalette = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCommand, setActiveCommand] = useState(null);
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [commandResults, setCommandResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const listContainerRef = useRef(null);

  // Key event handler to open command palette on cmd+k / ctrl+k
  const handleKeyDown = useCallback((event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      setIsOpen(true);
      setActiveCommand(null);
      setSelectedIndex(-1);
    }

    // Handle escape key to close or go back
    if (event.key === 'Escape') {
      if (activeCommand) {
        setActiveCommand(null);
        setSearchTerm('');
        setSelectedIndex(-1);
      } else {
        setIsOpen(false);
      }
    }
  }, [activeCommand]);

  // Component mount effects
  useEffect(() => {
    // Setup event listeners for keyboard shortcuts
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Auto-highlight single result
  useEffect(() => {
    const currentResults = activeCommand === COMMANDS.SEARCH_ORGS ? filteredOrgs : commandResults;
    if (currentResults.length === 1) {
      setSelectedIndex(0);
    }
  }, [commandResults, filteredOrgs, activeCommand]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to ensure DOM is ready
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 10);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, activeCommand]);

  // Reset selected index when filtered results change
  useEffect(() => {
    if (commandResults.length !== 1 && filteredOrgs.length !== 1) {
      setSelectedIndex(-1);
    }
  }, [commandResults, filteredOrgs]);

  // Scroll selected item into view when selectedIndex changes
  useEffect(() => {
    if (selectedIndex === -1 || !listContainerRef.current) return;
    
    setTimeout(() => {
      const currentResults = activeCommand === COMMANDS.SEARCH_ORGS ? filteredOrgs : commandResults;
      if (currentResults.length === 0) return;
      
      const listContainer = listContainerRef.current;
      if (!listContainer) return;
      
      const items = listContainer.querySelectorAll('.command-palette-list-item');
      if (!items || items.length === 0 || selectedIndex >= items.length) return;
      
      const selectedItem = items[selectedIndex];
      if (!selectedItem) return;
      
      // Scroll the item into view with a small buffer
      selectedItem.scrollIntoView({ block: 'nearest' });
    }, 10);
  }, [selectedIndex, activeCommand, filteredOrgs, commandResults]);

  // Filter commands or orgs based on search term
  useEffect(() => {
    if (!activeCommand) {
      // Filter available commands
      if (!searchTerm) {
        setCommandResults(COMMAND_DEFINITIONS);
      } else {
        const filtered = COMMAND_DEFINITIONS.filter(
          (command) => 
            command.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            command.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setCommandResults(filtered);
      }
    } else if (activeCommand === COMMANDS.SEARCH_ORGS) {
      // Filter organizations
      if (props.user?.accounts) {
        if (!searchTerm) {
          setFilteredOrgs(props.user.accounts);
        } else {
          const filtered = props.user.accounts.filter(
            (org) => org.id.toLowerCase().includes(searchTerm.toLowerCase())
          );
          setFilteredOrgs(filtered);
        }
      }
    }
  }, [searchTerm, activeCommand, props.user?.accounts]);

  // Function to handle command selection
  const handleCommandSelect = (commandId) => {
    setActiveCommand(commandId);
    setSearchTerm('');
    setSelectedIndex(-1);
  };

  // Function to navigate to the org dashboard and close the palette
  const handleOrgSelect = (orgId) => {
    props.history.push(`/account/${orgId}`);
    setIsOpen(false);
    setSearchTerm('');
    setActiveCommand(null);
  };

  // Handle close
  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm('');
    setActiveCommand(null);
    setSelectedIndex(-1);
  };

  const getAbrAccount = (id) => {
    const firstChar = id.substring(0, 3);
    let secondChar = '';
    const split = id.split('-');
    if (split[1]) {
      secondChar = split[1].substring(1, 0);
    }
    return `${firstChar}`;
  };

  // Handle keyboard navigation in the command palette
  const handleInputKeyDown = (e) => {
    const currentResults = activeCommand === COMMANDS.SEARCH_ORGS ? filteredOrgs : commandResults;
    const maxIndex = currentResults.length - 1;

    // Down arrow key
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prevIndex) => {
        return prevIndex < maxIndex ? prevIndex + 1 : 0;
      });
    }
    
    // Up arrow key
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prevIndex) => {
        // If no selection, start from bottom when going up
        if (prevIndex === -1) return maxIndex;
        return prevIndex > 0 ? prevIndex - 1 : maxIndex;
      });
    }
    
    // Enter key for selection
    else if (e.key === 'Enter') {
      e.preventDefault();
      
      if (currentResults.length === 0) return;
      
      // If only one result and in org search, select it automatically
      if (activeCommand === COMMANDS.SEARCH_ORGS && currentResults.length === 1) {
        handleOrgSelect(currentResults[0].id);
        return;
      }
      
      // Select the highlighted item
      if (activeCommand === COMMANDS.SEARCH_ORGS) {
        if (selectedIndex >= 0 && selectedIndex < currentResults.length) {
          handleOrgSelect(currentResults[selectedIndex].id);
        }
      } else {
        if (selectedIndex >= 0 && selectedIndex < currentResults.length) {
          handleCommandSelect(currentResults[selectedIndex].id);
        }
      }
    }
  };

  // Render main command list or specific command interface
  const renderContent = () => {
    if (activeCommand === COMMANDS.SEARCH_ORGS) {
      return (
        <>
          <div className="command-palette-header">
            <div className="back-button" onClick={() => { setActiveCommand(null); setSearchTerm(''); }}>
              ← Back
            </div>
            <div className="command-title">Search Organizations</div>
          </div>
          <Input
            ref={inputRef}
            placeholder="Search for an organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleInputKeyDown}
            className="command-palette-input"
            suffix={null}
            bordered={false}
            autoFocus={true}
          />
          <div 
            ref={listContainerRef}
            className="list-container org-results-list"
          >
            <List
              className="command-palette-list"
              itemLayout="horizontal"
              dataSource={filteredOrgs}
              renderItem={(item, index) => (
                <List.Item 
                  onClick={() => handleOrgSelect(item.id)}
                  className={`command-palette-list-item ${index === selectedIndex ? 'selected' : ''}`}
                >
                  <List.Item.Meta
                    avatar={
                      item.icon ? (
                        <Avatar 
                          src={`https://assets.akuko.io/${item.icon}`} 
                          className="org-avatar" 
                        />
                      ) : (
                        <Avatar className="org-avatar-default">
                          {getAbrAccount(item.id)}
                        </Avatar>
                      )
                    }
                    title={item.id}
                  />
                </List.Item>
              )}
            />
          </div>
        </>
      );
    }

    // Default command list
    return (
      <>
        <Input
          ref={inputRef}
          placeholder="Type a command or search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="command-palette-input"
          prefix={<SearchOutlined className="search-icon" />}
          suffix={null}
          bordered={false}
          autoFocus={true}
        />
        <div 
          ref={listContainerRef}
          className="list-container"
        >
          <List
            className="command-palette-list"
            itemLayout="horizontal"
            dataSource={commandResults}
            renderItem={(command, index) => (
              <List.Item 
                onClick={() => handleCommandSelect(command.id)}
                className={`command-palette-list-item ${index === selectedIndex ? 'selected' : ''}`}
              >
                <List.Item.Meta
                  avatar={command.icon}
                  title={command.title}
                  description={command.description}
                />
                <Text type="secondary" className="command-shortcut">{command.shortcut}</Text>
              </List.Item>
            )}
          />
        </div>
      </>
    );
  };

  return (
    <Modal
      title={null}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      className="command-palette-modal"
      maskClosable={true}
      destroyOnClose={true}
      width={500}
      centered={true}
      closeIcon={null}
      focusTriggerAfterClose={false}
      zIndex={1050}
    >
      <div className="command-palette-container">
        {renderContent()}
      </div>
    </Modal>
  );
};

const CommandPaletteContainer = connect(mapStateToProps)(CommandPalette);

export default withRouter(CommandPaletteContainer); 